import { BASE_PADRAO, baseDeIncidencia, percentuaisVariaveis } from '@favo/core';
import { ArrowRight, CircleHelp, Plus, X } from 'lucide-react';
import * as React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { CampoMoeda } from '@/componentes/campo-moeda';
import { CampoPercentual, EntradaPercentual } from '@/componentes/campo-percentual';
import { Camada, Celula } from '@/componentes/celula';
import { Falha } from '@/componentes/estado-da-tela';
import { RodapeFunil } from '@/componentes/rodape-funil';
import { Segmentado, type OpcaoSegmentada } from '@/componentes/segmentado';
import { Button } from '@/componentes/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/componentes/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/componentes/ui/dialog';
import { Input } from '@/componentes/ui/input';
import { Separator } from '@/componentes/ui/separator';
import { Skeleton } from '@/componentes/ui/skeleton';
import { Valor } from '@/componentes/valor';
import type { BaseIncidencia, Despesas as BlocoDespesas } from '@/dados';
import { mensagemDoErro, useIntegrador, useSalvarIntegrador } from '@/dados';
import { parseNum } from '@/design/formato';
import { Pagina } from '@/layout/pagina';

/* Despesas: o que sai de cada venda (variável) e o que sai todo mês (fixa).
 *
 * Duas grandezas com unidades diferentes convivem aqui, e a distinção é o
 * ponto da tela: percentual em PONTOS (o usuário digita 12 para 12%) e
 * dinheiro em reais. Por isso percentual usa type=number com parseFloat, e
 * dinheiro usa CampoMoeda (máscara pt-BR).
 *
 * O bloco jsonb `despesas` é gravado inteiro pelo PATCH; as demais colunas do
 * integrador não são tocadas. */

const BASES: ReadonlyArray<OpcaoSegmentada<BaseIncidencia>> = [
  { valor: 'total', rotulo: 'Sobre o total', descricao: 'Incide sobre o preço de venda inteiro.' },
  {
    valor: 'servico',
    rotulo: 'Sobre o serviço',
    descricao: 'Incide só sobre o preço menos o kit, que é faturado pela distribuidora.',
  },
];

/** Linha de despesa extra em edição. `chave` só existe para o React. */
type ExtraEditavel = { chave: number; nome: string; percentual: string; base: BaseIncidencia };

/** Lê o campo de percentual (type=number, decimal com ponto). Vazio vale 0. */
function pontosDe(texto: string): number {
  const n = parseFloat(texto);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * jsonb para texto de campo. O valor gravado pode ser número (escrita nova) ou
 * texto pt-BR (jsonb legado); `parseNum` lê os dois, exatamente como o motor lê
 * este mesmo bloco. Não confundir com a leitura do INPUT, que é parseFloat.
 */
function textoDoPercentual(bruto: unknown): string {
  if (bruto === null || bruto === undefined || bruto === '') return '';
  return String(parseNum(bruto));
}

export function Despesas() {
  const integrador = useIntegrador();
  const salvar = useSalvarIntegrador();
  const navegar = useNavigate();
  const [busca] = useSearchParams();

  // Aberta pelo painel (?retorno=dashboard), a tela deixa de ser etapa do
  // funil: salvar volta ao painel e o rodapé perde o par voltar/continuar.
  const retornoPainel = busca.get('retorno') === 'dashboard';

  const [comissao, setComissao] = React.useState('');
  const [comissaoBase, setComissaoBase] = React.useState<BaseIncidencia>(BASE_PADRAO.comissao);
  const [tributo, setTributo] = React.useState('');
  const [tributoBase, setTributoBase] = React.useState<BaseIncidencia>(BASE_PADRAO.tributo);
  const [extras, setExtras] = React.useState<ExtraEditavel[]>([]);
  const [outras, setOutras] = React.useState('');
  const [despFixa, setDespFixa] = React.useState<number | null>(null);

  const proximaChave = React.useRef(0);
  const hidratado = React.useRef(false);

  // Uma leitura só: o formulário nasce com o que veio do banco e depois é dono
  // do próprio estado. Sem esta guarda, um refetch apagaria a digitação.
  const linha = integrador.data;
  React.useEffect(() => {
    // Erro não hidrata: o formulário não pode nascer vazio e depois recusar o
    // dado que chegar num refetch bem-sucedido.
    if (hidratado.current || integrador.isPending || integrador.isError) return;
    hidratado.current = true;

    const d: BlocoDespesas = linha?.despesas ?? {};
    setComissao(textoDoPercentual(d.comissao?.valor));
    setComissaoBase(baseDeIncidencia(d.comissao?.base, BASE_PADRAO.comissao));
    setTributo(textoDoPercentual(d.tributo?.valor));
    setTributoBase(baseDeIncidencia(d.tributo?.base, BASE_PADRAO.tributo));
    setOutras(textoDoPercentual(d.outras));
    setDespFixa(d.despFixa === null || d.despFixa === undefined ? null : parseNum(d.despFixa));
    setExtras(
      (d.extras ?? []).map((e) => ({
        chave: proximaChave.current++,
        nome: e?.nome ?? '',
        percentual: textoDoPercentual(e?.valor),
        base: baseDeIncidencia(e?.base, BASE_PADRAO.extra),
      })),
    );
  }, [integrador.isPending, integrador.isError, linha]);

  // Soma dos percentuais pelo motor, não por conta desta tela: é a mesma
  // função que o preço sugerido usa como denominador.
  const soma = percentuaisVariaveis({
    comissao: pontosDe(comissao),
    comissaoBase,
    tributo: pontosDe(tributo),
    tributoBase,
    outras: pontosDe(outras),
    despesasExtras: extras.map((e) => ({
      nome: e.nome.trim() || 'Extra',
      valor: pontosDe(e.percentual),
      base: e.base,
    })),
  });

  function acrescentarExtra() {
    setExtras((atuais) => [
      ...atuais,
      { chave: proximaChave.current++, nome: '', percentual: '', base: BASE_PADRAO.extra },
    ]);
  }

  function mudarExtra(chave: number, mudanca: Partial<Omit<ExtraEditavel, 'chave'>>) {
    setExtras((atuais) => atuais.map((e) => (e.chave === chave ? { ...e, ...mudanca } : e)));
  }

  function removerExtra(chave: number) {
    setExtras((atuais) => atuais.filter((e) => e.chave !== chave));
  }

  async function enviar() {
    const bloco: BlocoDespesas = {
      comissao: { valor: pontosDe(comissao), base: comissaoBase },
      tributo: { valor: pontosDe(tributo), base: tributoBase },
      // Linha sem nome é linha aberta e não preenchida: cobrar por ela seria
      // pôr no relatório uma despesa anônima.
      extras: extras
        .filter((e) => e.nome.trim() !== '')
        .map((e) => ({ nome: e.nome.trim(), valor: pontosDe(e.percentual), base: e.base })),
      outras: pontosDe(outras),
      despFixa: despFixa ?? 0,
    };

    try {
      await salvar.mutateAsync({ despesas: bloco });
      navegar(retornoPainel ? '/' : '/sazonalidade');
    } catch (erro) {
      toast.error(mensagemDoErro(erro));
    }
  }

  const semEmpresa = !integrador.isPending && !integrador.isError && linha == null;

  return (
    <Pagina
      titulo="Despesas"
      descricao="O que sai de cada venda e o que a empresa paga todo mês."
      etapa="despesas"
      acoes={<AjudaVariavelOuFixa />}
    >
      {integrador.isPending ? (
        <Carregando />
      ) : integrador.isError ? (
        <Falha
            oQue="as despesas"
            motivo={mensagemDoErro(integrador.error)}
            aoTentarDeNovo={() => void integrador.refetch()}
          />
      ) : (
        <div className="favo">
          {semEmpresa ? (
            <Celula respiro="justo" className="bg-atencao-tinta text-apoio">
              Cadastre a empresa em <Link className="text-acao underline" to="/empresa">Empresa</Link> antes
              de configurar as despesas.
            </Celula>
          ) : null}

          <Card>
            <CardHeader>
              <CardTitle>Despesas variáveis</CardTitle>
              <CardDescription>
                Percentuais que acompanham o faturamento. Só existem quando há venda.
              </CardDescription>
            </CardHeader>

            <CardContent className="grid gap-6">
              {/* Campo e base formam UMA unidade: o seletor diz sobre o que
                  aquele percentual incide, então mora embaixo do próprio
                  campo. Lado a lado ele parecia pertencer ao campo vizinho. */}
              {/* Lado a lado a partir de 900px, METADE do cartão para cada
                  um. O limite de largura de leitura que existia aqui vinha da
                  época em que a etapa ocupava 1360px: com o casco de
                  formulário a coluna já fecha em 900, e o limite passou a
                  fazer o contrário do que fazia, empurrando a dupla para o
                  canto esquerdo com um terço do cartão vazio à direita.
                  Quem limita o CONTROLE continua sendo a natureza do dado
                  grade da tela; o controle preenche a célula. */}
              <div className="grid gap-6 @campos:grid-cols-2 @campos:gap-x-8">
                <div className="grid content-start gap-2">
                  <CampoPercentual
                    rotulo="Comissão de vendas"
                    valor={comissao}
                    aoMudar={setComissao}
                    max={100}
                  />
                  <Segmentado
                    opcoes={BASES}
                    valor={comissaoBase}
                    aoMudar={setComissaoBase}
                    tamanho="pequeno"
                    nome="base-comissao"
                    rotulo="Base de incidência da comissão de vendas"
                    rotuloOculto
                    className="gap-0 self-start"
                  />
                </div>

                <div className="grid content-start gap-2">
                  <CampoPercentual
                    rotulo="Carga tributária média"
                    ajuda="Alíquota efetiva do regime da empresa."
                    valor={tributo}
                    aoMudar={setTributo}
                    max={100}
                  />
                  <Segmentado
                    opcoes={BASES}
                    valor={tributoBase}
                    aoMudar={setTributoBase}
                    tamanho="pequeno"
                    nome="base-tributo"
                    rotulo="Base de incidência da carga tributária"
                    rotuloOculto
                    className="gap-0 self-start"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid gap-3">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="titulo-bloco">Outras despesas variáveis</h3>
                  <Button variant="outline" size="sm" onClick={acrescentarExtra}>
                    <Plus />
                    Adicionar despesa
                  </Button>
                </div>

                {extras.length === 0 ? (
                  <p className="apoio">
                    Nenhuma cadastrada. Use para marketing, taxa de cartão, royalties.
                  </p>
                ) : (
                  <ul className="grid">
                    {extras.map((extra) => (
                      <li
                        key={extra.chave}
                        className="border-border grid grid-cols-[1fr_auto] items-center gap-x-3 gap-y-2 border-t py-3 @campos:grid-cols-[minmax(0,1fr)_auto_auto_auto]"
                      >
                        <Input
                          value={extra.nome}
                          onChange={(evento) => mudarExtra(extra.chave, { nome: evento.currentTarget.value })}
                          placeholder="Nome da despesa"
                          aria-label="Nome da despesa variável"
                        />
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          onClick={() => removerExtra(extra.chave)}
                          aria-label={`Remover ${extra.nome.trim() || 'despesa sem nome'}`}
                          className="justify-self-end mesa:order-last"
                        >
                          <X />
                        </Button>
                        <EntradaPercentual
                          valor={extra.percentual}
                          aoMudar={(v) => mudarExtra(extra.chave, { percentual: v })}
                          aria-label="Percentual da despesa variável"
                        />
                        <Segmentado
                          opcoes={BASES}
                          valor={extra.base}
                          aoMudar={(base) => mudarExtra(extra.chave, { base })}
                          tamanho="pequeno"
                          nome={`base-extra-${extra.chave}`}
                          rotulo={`Base de incidência de ${extra.nome.trim() || 'despesa sem nome'}`}
                          rotuloOculto
                          className="col-span-2 gap-0 @campos:col-span-1"
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Campo solto também mora em METADE do cartão: fora da grade
                  ele herda a largura inteira, e a linha de ajuda atravessa o
                  cartão enquanto o controle tem 112px. A grade é o que dá o
                  mesmo eixo a todos os campos da tela. */}
              <div className="grid gap-6 @campos:grid-cols-2 @campos:gap-x-8">
                <CampoPercentual
                  rotulo="Percentual adicional (não listado)"
                  ajuda="Percentual livre para o que não está na lista. Deixe 0 se não houver."
                  valor={outras}
                  aoMudar={setOutras}
                  max={100}
                />
              </div>

              {/* Areia: resumo que constata, não opina. O raio vem do respiro
                  do cartão pela regra concêntrica da Camada. */}
              <Camada className="bg-areia grid gap-1">
                <span className="rotulo">Incidência somada</span>
                <p className="text-apoio">
                  <Valor valor={soma.total} formato="percentual" /> sobre o total e{' '}
                  <Valor valor={soma.serv} formato="percentual" /> sobre o serviço, em cada venda.
                </p>
              </Camada>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Despesas fixas</CardTitle>
              <CardDescription>
                O custo mensal que existe mesmo em mês sem venda.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-6 @campos:grid-cols-2 @campos:gap-x-8">
              <CampoMoeda
                rotulo="Despesa fixa mensal total"
                ajuda="Folha e encargos, aluguel, software, pró-labore. Informe a média dos últimos seis meses."
                valor={despFixa}
                aoMudar={setDespFixa}
              />
            </CardContent>
          </Card>

          <RodapeFunil
            etapa="despesas"
            {...(retornoPainel ? { voltar: '/', rotuloVoltar: 'Voltar ao painel' } : {})}
          >
            <Button onClick={() => void enviar()} disabled={salvar.isPending || semEmpresa}>
              {salvar.isPending ? 'Salvando' : retornoPainel ? 'Salvar' : 'Continuar'}
              {salvar.isPending || retornoPainel ? null : <ArrowRight />}
            </Button>
          </RodapeFunil>
        </div>
      )}
    </Pagina>
  );
}

function AjudaVariavelOuFixa() {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm">
          <CircleHelp />
          Variável ou fixa
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Despesa variável e despesa fixa</DialogTitle>
          <DialogDescription>Onde cada gasto da empresa entra no preço.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 text-apoio">
          <p>
            <strong>Variável</strong> só existe quando há venda: some no mês em que não se vende e
            cresce junto com o faturamento. Comissão, imposto sobre a nota e taxa de cartão são
            variáveis, e por isso entram como percentual.
          </p>
          <p>
            <strong>Fixa</strong> existe mesmo sem venda: folha, aluguel, software, pró-labore.
            Entra em reais por mês, não em percentual.
          </p>
          <p>
            A fixa vira custo por projeto pelo volume: total do ano dividido pelos projetos do ano.
            É a etapa Sazonalidade que informa esse volume.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Carregando() {
  return (
    <div className="favo">
      <Skeleton className="h-48 w-full rounded-celula" />
      <Skeleton className="h-32 w-full rounded-celula" />
    </div>
  );
}
